import { BottomBar, SaveBar, SavePanel } from '@salecycle/ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUnloadTab from 'sections/hooks/useUnloadTab';
import useNotifications from 'core/notifications/useNotifications';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { useSaveRcsTemplate } from '../hooks/useSaveRcsTemplate';
import { useRestoreRcsTemplateVersion } from '../hooks/useRestoreRcsTemplateVersion';
import { hasValidationErrors, validateRcsConfig } from '../utils/validateRcsConfig';
import { rcsConfigToCompilerTemplateValue } from '../utils/rcsConfigToCardContent';
import { RcsTemplateEditorUnsavedChangesModal } from './modals/RcsTemplateEditorUnsavedChangesModal';

/** Bottom toolbar for the RCS template editor: save, discard, exit, restore when previewing version, and unsaved-changes modal. */
export const RcsTemplateEditorBottomToolbar = () => {
  const { t } = useTranslation('designs');
  const { showNotification } = useNotifications();
  const {
    hasUnsavedChanges,
    rcsConfig,
    templateName,
    templateType,
    selectedLanguage,
    enrichmentConfigIds,
    previewingVersion,
  } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const { saveMutation, redirectUrl, history, siteId, templateId, routeLanguage } = useSaveRcsTemplate();
  const { restoreTemplateVersion, isRestoring } = useRestoreRcsTemplateVersion();
  const isPreviewMode = previewingVersion.length > 0;

  const validationErrors = validateRcsConfig(rcsConfig);
  const formIsValid = !hasValidationErrors(validationErrors);
  const canSave =
    Boolean(siteId && templateId && routeLanguage) && hasUnsavedChanges && formIsValid && !saveMutation.isLoading;

  useUnloadTab(hasUnsavedChanges);

  const handleSaveButtonClicked = () => {
    if (!formIsValid) {
      dispatch({ type: 'SET_RCS_VALIDATION_ERRORS', payload: { validationErrors } });
      showNotification(t('rcsTemplateEditor.saveValidationError'), 'error');
      return;
    }
    dispatch({ type: 'SET_RCS_VALIDATION_ERRORS', payload: { validationErrors: {} } });
    if (!siteId || !templateId || !routeLanguage) {
      showNotification(t('designs:designGallery.templateEditor.queries.save.error'), 'error');
      return;
    }
    const templateBody = rcsConfigToCompilerTemplateValue(rcsConfig);
    saveMutation.mutate({
      siteId,
      templateId,
      templateName,
      templateBody,
      language: selectedLanguage || routeLanguage,
      type: templateType,
      enrichmentConfigIds: enrichmentConfigIds ?? [],
      rcsConfigSnapshot: rcsConfig,
      t,
    });
  };

  const handleExitButtonClicked = () => {
    if (hasUnsavedChanges) {
      setUnsavedChangesModalOpen(true);
    } else {
      history.push(redirectUrl);
    }
  };

  const handleDiscardButtonClicked = () => {
    dispatch({ type: 'DISCARD_CHANGES' });
  };

  return (
    <>
      <BottomBar>
        {isPreviewMode ? (
          <SavePanel
            onSaveButtonClicked={restoreTemplateVersion}
            saveButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.restoreTemplate')}
            enableSaveButton={!isRestoring}
            paddingLeft="13px"
          />
        ) : (
          <SaveBar
            onSaveButtonClicked={handleSaveButtonClicked}
            onExitButtonClicked={handleExitButtonClicked}
            saveButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.saveTemplate')}
            exitButtonLabel={t('designs:designGallery.templateEditor.bottomToolbar.exit')}
            onDiscardButtonClicked={handleDiscardButtonClicked}
            enableSaveButton={canSave}
            paddingLeft="13px"
          />
        )}
      </BottomBar>
      <RcsTemplateEditorUnsavedChangesModal
        unsavedChangesModalOpen={unsavedChangesModalOpen}
        setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
        redirectUrl={redirectUrl}
      />
    </>
  );
};
